import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import 'pdfjs-dist/build/pdf.worker.entry'
import './PdfViewer.scss'
import ZoomIn from '../svg/zoom-in'
import ZoomOut from '../svg/zoom-out'
import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js'
import Icon from './Icon'
import Loader from './Loader'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

const PdfViewer = ({ pdfUrl }) => {
  const [pdf, setPdf] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [scale, setScale] = useState(1.0)
  const [renderedPages, setRenderedPages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef(null)
  const pageInputRef = useRef(null)
  const canvasRefs = useRef({})

  useEffect(() => {
    const loadPdf = async() => {
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl)
        const pdfDocument = await loadingTask.promise
        setPdf(pdfDocument)
        setNumPages(pdfDocument.numPages)
      } catch (error) {
        console.error('Error loading PDF:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadPdf()
  }, [pdfUrl])

  useEffect(() => {
    const renderPage = async(pageNum, page, viewport) => {
      const canvas = canvasRefs.current[pageNum]
      if (!canvas) return

      const context = canvas.getContext('2d')
      const outputScale = window.devicePixelRatio || 1

canvas.width = Math.floor(viewport.width * outputScale)
canvas.height = Math.floor(viewport.height * outputScale)
canvas.style.width = `${viewport.width}px`
canvas.style.height = `${viewport.height}px`

const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null

await page.render({
  canvasContext: context,
  viewport: viewport,
  transform: transform
}).promise
    }

    const renderPages = async() => {
      if (!pdf || !containerRef.current) return

      const pages = []
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i)
        const viewport = page.getViewport({ scale })

        pages.push(
          <div key={i} id={`page-${i}`} className="pdf-page-container">
            <canvas
              ref={el => {
                canvasRefs.current[i] = el
                if (el) {
                  renderPage(i, page, viewport)
                }
              }}
            />
          </div>
        )
      }
      setRenderedPages(pages)
    }

    renderPages()
  }, [pdf, scale, numPages])

  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.25, 3.0))
  }

  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.25, 0.5))
  }

  const handlePageChange = (e) => {
    const pageNum = parseInt(e.target.value)
    if (pageNum >= 1 && pageNum <= numPages) {
      scrollToPage(pageNum)
    }
  }

  const scrollToPage = useCallback((pageNum) => {
    setCurrentPage(pageNum)
    const container = containerRef.current
    const pageElement = document.getElementById(`page-${pageNum}`)
    if (container && pageElement) {
      const containerTop = container.getBoundingClientRect().top
      const pageTop = pageElement.getBoundingClientRect().top
      const scrollOffset = pageTop - containerTop + container.scrollTop
      container.scrollTo({ top: scrollOffset, behavior: 'smooth' })
    }
  }, [])

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = 'document.pdf'
    link.click()
  }

  const handleScroll = () => {
    const container = containerRef.current
    if (!container) return

    const pageElements = Object.keys(canvasRefs.current).map(pageNum => {
      const el = document.getElementById(`page-${pageNum}`)
      return { pageNum: Number(pageNum), top: el?.getBoundingClientRect().top }
    })

    const offsetY = container.getBoundingClientRect().top

    const visiblePage = pageElements.reduce((closest, page) => {
      const distance = Math.abs(page.top - offsetY)
      return distance < closest.distance ? { pageNum: page.pageNum, distance } : closest
    }, { pageNum: 1, distance: Infinity })

    setCurrentPage(visiblePage.pageNum)
  }

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <div className="pdf-viewer">
      <div className="pdf-toolbar">
        <div className="page-controls">
          <span>
            Page <input 
              type="number" 
              value={currentPage} 
              onChange={handlePageChange}
              min="1"
              max={numPages}
              ref={pageInputRef}
            /> / {numPages}
          </span>
          <button 
            onClick={() => scrollToPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className='arrow-down'
          >
            <Icon type='arrowLeft' />
          </button>
          <button 
            onClick={() => scrollToPage(Math.min(numPages, currentPage + 1))}
            disabled={currentPage === numPages}
            className='arrow-up'
          >
            <Icon type='arrowLeft' />
          </button>
        </div>
        <div className="zoom-controls">
          <span>{Math.round(scale * 100)}%</span>
          <button onClick={handleZoomOut}>
            <ZoomOut width={20} height={20} />
          </button>
          <button onClick={handleZoomIn}>
            <ZoomIn width={20} height={20} />
          </button>
        </div>
        <button className='download-button' onClick={handleDownload}>
          <Icon type='downloadPdf' />
        </button>
      </div>

      <div 
        className="pdf-container" 
        ref={containerRef}
        style={{ overflow: 'auto', height: 'calc(100vh - 100px)' }}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', marginTop: '20px' }}><Loader className="inverse"/></div>
        ) : (
          renderedPages
        )}
      </div>
    </div>
  )
}

PdfViewer.propTypes = {
  pdfUrl: PropTypes.string.isRequired
}

export default PdfViewer